import { type BrandKey } from '@jotta/design-system/system'

////////////////////////////////////////////////////////////
/////////////////////// TYPES //////////////////////////////
////////////////////////////////////////////////////////////
type FontSizesThemeUI = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
]

type FontSizesTailwind = Record<
  'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl',
  string
>

////////////////////////////////////////////////////////////
/////////////////////// CONFIG //////////////////////////////
////////////////////////////////////////////////////////////

const fontConfig: Record<
  BrandKey,
  {
    customFont?: string
    customHeading?: string
    fontSizes?: FontSizesThemeUI
  }
> = {
  test: {},
  jottacloud: {},
  elkjop: {},
  elgiganten: {},
  gigantti: {},
  elko: {},
  mediamarkt: {
    customFont: 'SourceSansPro',
  },
  saturn: {
    customFont: 'SourceSansPro',
  },
  tele2: {
    customFont: 'Tele2Sans',
  },
  teliasky: {
    customFont: 'TeliaSans',
  },
  teliacloud: {
    customFont: 'TeliaSans',
  },
  tsky: {},
  onlime: {
    customHeading: 'Coolvetica',
  },
  obos: {
    customFont: 'Gordita',
    fontSizes: [12, 13, 14, 16, 20, 24, 32, 48, 64],
  },
  halebop: {
    customFont: 'halebopmedium',
    fontSizes: [12, 13, 15, 16, 18, 20, 24, 48, 64],
  },
  phonero: {},
  letsgo: {},
}

////////////////////////////////////////////////////////////
/////////////////////// FONTS //////////////////////////////
////////////////////////////////////////////////////////////

const standardFonts = [
  'Inter',
  'system-ui',
  '-apple-system',
  'BlinkMacSystemFont',
  'Segoe UI',
  'Roboto',
  'Helvetica Neue',
  'Arial',
  'sans-serif',
]

const countryFlagEmojiPolyfill = 'Twemoji Country Flags'

const resolveFonts = (customFont?: string) => {
  return [countryFlagEmojiPolyfill, customFont, ...standardFonts]
    .filter(ff => ff)
    .map(lambda => `"${lambda}"`)
    .join(',')
}

export const resolveFontFamily = (brandKey: BrandKey) => {
  const customBody = fontConfig[brandKey].customFont
  const customHeading = fontConfig[brandKey].customHeading
  return {
    body: resolveFonts(customBody),
    heading: resolveFonts(customHeading || customBody),
    monospace: 'Menlo, monospace',
  }
}

////////////////////////////////////////////////////////////
///////////////////// FONT SIZES ///////////////////////////
////////////////////////////////////////////////////////////

const standardFontSizes: FontSizesThemeUI = [
  12, // xs
  13, // sm
  15, // base
  16, // lg
  19, // xl
  24, // 2xl
  32, // 3xl
  48, // 4xl
  64, // 5xl
]

export const resolveThemeUIFontSizes = (
  brandKey: BrandKey,
): FontSizesThemeUI => {
  const fontSizes = fontConfig[brandKey].fontSizes || standardFontSizes
  return fontSizes
}

export const resolveTailwindFontSizes = (
  brandKey: BrandKey,
): FontSizesTailwind => {
  const fontSizes = fontConfig[brandKey].fontSizes || standardFontSizes

  const keys = [
    'xs',
    'sm',
    'base',
    'lg',
    'xl',
    '2xl',
    '3xl',
    '4xl',
    '5xl',
  ] as const

  const remSizes = fontSizes.map(size => `${(size / 16).toFixed(4)}rem`)

  return keys.reduce((acc, key, index) => {
    acc[key] = remSizes[index]
    return acc
  }, {} as FontSizesTailwind)
}
